import React, { useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

interface Token {
  symbol: string;
  name: string;
  logoURI?: string;
  balance?: string;
  price?: number;
}

const popularTokens: Token[] = [
  { symbol: 'ETH', name: 'Ethereum', balance: '5.2847', price: 2845.67 },
  { symbol: 'USDC', name: 'USD Coin', balance: '1,234.56', price: 1.00 },
  { symbol: 'USDT', name: 'Tether USD', balance: '0', price: 1.00 },
  { symbol: 'BTC', name: 'Bitcoin', balance: '0.1234', price: 67432.89 },
  { symbol: 'NDH', name: 'NDH Token', balance: '10,000', price: 0.89 },
  { symbol: 'UNI', name: 'Uniswap', balance: '25.5', price: 12.45 },
  { symbol: 'LINK', name: 'Chainlink', balance: '0', price: 15.67 },
  { symbol: 'AAVE', name: 'Aave', balance: '0', price: 89.12 },
];

interface TokenSelectorProps {
  selectedToken: string;
  amount: string;
  onTokenSelect: (token: string) => void;
  onAmountChange: (amount: string) => void;
  balance: string;
  isInput: boolean;
}

export function TokenSelector({
  selectedToken,
  amount,
  onTokenSelect,
  onAmountChange,
  balance,
  isInput,
}: TokenSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const selectedTokenData = popularTokens.find(t => t.symbol === selectedToken);
  
  const filteredTokens = popularTokens.filter(token =>
    token.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
    token.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleTokenSelect = (token: Token) => {
    onTokenSelect(token.symbol);
    setOpen(false);
  };

  const handleMaxClick = () => {
    if (selectedTokenData?.balance) {
      onAmountChange(selectedTokenData.balance.replace(',', ''));
    }
  };

  return (
    <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800">
      <div className="flex items-center justify-between mb-2">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="ghost" className="h-auto p-2 font-medium">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                  {selectedToken.slice(0, 2)}
                </div>
                <span>{selectedToken}</span>
                <ChevronDown className="h-4 w-4" />
              </div>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Select Token</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search tokens..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <div className="max-h-64 overflow-y-auto space-y-1">
                {filteredTokens.map((token) => (
                  <button
                    key={token.symbol}
                    onClick={() => handleTokenSelect(token)}
                    className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                        {token.symbol.slice(0, 2)}
                      </div>
                      <div className="text-left">
                        <div className="font-medium">{token.symbol}</div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">
                          {token.name}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{token.balance || '0'}</div>
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        ${token.price?.toFixed(2)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {isInput && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMaxClick}
            className="h-6 px-2 text-xs"
          >
            MAX
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between">
        <Input
          type="number"
          placeholder="0.0"
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
          className="border-0 p-0 text-2xl font-semibold bg-transparent focus-visible:ring-0"
          readOnly={!isInput}
        />
        <div className="text-right">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Balance: {balance}
          </div>
          {amount && selectedTokenData?.price && (
            <div className="text-sm text-slate-500 dark:text-slate-400">
              ≈ ${(parseFloat(amount) * selectedTokenData.price).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}